import { useState, useEffect } from 'react';
import { getTags } from '@/services/apis/modules/home';
import { useGetTagChartData } from '@/helpers/useGetTagChartData';
import { HomeTags } from '@/interfaces/home';
import { InitialData } from '../types';

export const useIndicatorSession = (
  slug: string,
  screen: string,
  slugData?: any,
) => {
  const [tags, setTags] = useState<HomeTags>();
  const [isLoading, setIsLoading] = useState(true);
  const [activeId, setActiveId] = useState('');
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined,
  );
  const [searchData, setSearchData] = useState('');
  const [type, setType] = useState('');
  const [initialData, setInitialData] = useState<InitialData>({
    key: '',
    value: undefined,
  });

  const { chartData, loading, handleGetChardData, handleGetChartDataByRange } =
    useGetTagChartData();

  useEffect(() => {
    const controller = new AbortController();
    fetchTags();
    return () => {
      controller.abort();
    };
  }, [slug, screen]);

  async function fetchTags() {
    if (tags || slugData) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await getTags({ key: slug, screen: screen });
      setTags(response?.data || []);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setIsLoading(false);
    }
  }

  const dataSource = slugData || tags?.contextualIndicators;

  useEffect(() => {
    if (dataSource && dataSource?.length > 0) {
      const firstItem = dataSource?.[0];
      const firstItemId =
        firstItem?.indicator?.id ||
        firstItem?.industryIndicator?.id ||
        firstItem?.localIndicator?.id;
      const firstItemIsStructure =
        firstItem?.indicator?.isStructure ||
        firstItem?.industryIndicator?.isStructure ||
        firstItem?.localIndicator?.isStructure;
      const firstItemIsObjectOneTime =
        firstItem?.indicator?.isObjectOneTime ||
        firstItem?.industryIndicator?.isObjectOneTime ||
        firstItem?.localIndicator?.isObjectOneTime;

      if (firstItemId) {
        setActiveId(
          firstItem?.industryIndicator?.id ||
            firstItem?.indicator?.id ||
            firstItem?.localIndicator?.id,
        );
        handleGetChardData(
          firstItemId,
          firstItem?.industryIndicator?.type ||
            firstItem?.indicator?.type ||
            firstItem?.localIndicator?.type,
          firstItemIsStructure ? 1 : 0,
          '',
          firstItemIsObjectOneTime ? 1 : 0,
        );
      }
    }
  }, [dataSource]);

  useEffect(() => {
    if (selectedTime && activeId) {
      handleGetChardData(
        activeId,
        'local_indicator',
        chartData?.isStructure ? 1 : 0,
        selectedTime,
        chartData?.isObjectOneTime ? 1 : 0,
      );
    }
  }, [selectedTime]);

  useEffect(() => {
    const values = chartData?.values || {};
    for (const key in values) {
      if (Object.keys(values[key]).length > 0) {
        setInitialData({
          key,
          value: values[key],
        });
      }
    }
  }, [chartData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchData(e.target.value);
  };

  const handleSelectTime = (value: string) => {
    setSelectedTime(value);
  };

  return {
    tags,
    isLoading,
    activeId,
    setActiveId,
    selectedTime,
    searchData,
    type,
    setType,
    initialData,
    chartData,
    loading,
    handleGetChardData,
    handleGetChartDataByRange,
    handleInputChange,
    handleSelectTime,
    dataSource,
  };
};
